import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { formatSpeedToCareDuration } from "../utils/formatters";
const SpeedToCare = ({ interaction, showLegend = false, hideGraph = false, maxDuration = 1200, compact = false, showTooltipAsInfo = false, }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [hoverTimer, setHoverTimer] = useState(null);
    const calculateReadTime = () => {
        if (interaction.SecureMessageReadUtc && interaction.SecureMessageSentUtc) {
            try {
                const readTime = new Date(interaction.SecureMessageReadUtc).getTime();
                const sentTime = new Date(interaction.SecureMessageSentUtc).getTime();
                return Math.floor((readTime - sentTime) / 1000);
            }
            catch (error) {
                return 0;
            }
        }
        return 0;
    };
    const calculateCallbackTime = () => {
        if (interaction.CallbackFromMessageIdUtc && interaction.SecureMessageSentUtc) {
            try {
                const callbackTime = new Date(interaction.CallbackFromMessageIdUtc).getTime();
                const sentTime = new Date(interaction.SecureMessageSentUtc).getTime();
                return Math.floor((callbackTime - sentTime) / 1000);
            }
            catch (error) {
                return 0;
            }
        }
        return 0;
    };
    const callDuration = interaction.CallDurationSeconds || 0;
    const readTime = calculateReadTime();
    const callbackTime = calculateCallbackTime();
    const callbackDuration = interaction.CallbackDurationSeconds || 0;
    const adjustedCallbackTime = Math.max(0, callbackTime - readTime);
    const maxCallDuration = Math.max(callDuration, maxDuration * 0.3);
    const maxPostDuration = Math.max(readTime + adjustedCallbackTime + callbackDuration, maxDuration * 0.7);
    const getCallDurationWidth = () => {
        if (callDuration <= 0)
            return 0;
        return (callDuration / maxCallDuration) * 30;
    };
    const getReadTimeWidth = () => {
        if (readTime <= 0)
            return 0;
        return (readTime / maxPostDuration) * 70;
    };
    const getCallbackTimeWidth = () => {
        if (adjustedCallbackTime <= 0)
            return 0;
        return (adjustedCallbackTime / maxPostDuration) * 70;
    };
    const getCallbackDurationWidth = () => {
        if (callbackDuration <= 0)
            return 0;
        return (callbackDuration / maxPostDuration) * 70;
    };
    const totalTime = callDuration + callbackTime + callbackDuration;
    const hasMinimumData = callDuration > 0 || readTime > 0 || callbackTime > 0 || callbackDuration > 0;
    const handleMouseEnter = (e) => {
        setMousePosition({ x: e.clientX, y: e.clientY });
        if (hoverTimer) {
            clearTimeout(hoverTimer);
        }
        const timer = setTimeout(() => {
            setShowTooltip(true);
        }, 500);
        setHoverTimer(timer);
    };
    const handleMouseLeave = () => {
        if (hoverTimer) {
            clearTimeout(hoverTimer);
            setHoverTimer(null);
        }
        setShowTooltip(false);
    };
    const handleMouseMove = (e) => {
        setMousePosition({ x: e.clientX, y: e.clientY });
    };
    const PersistentInfo = () => {
        if (!showTooltipAsInfo)
            return null;
        return (_jsx("div", { style: {
                backgroundColor: "white",
                color: "#333",
                padding: "16px 20px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "500",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                border: "1px solid #e0e0e0",
                marginBottom: "12px",
            }, children: _jsxs("div", { className: "d-flex flex-column", style: { gap: "6px" }, children: [_jsx("div", { style: {
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#374B94",
                            marginBottom: "8px",
                        }, children: "Speed to Care" }), _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsxs("div", { className: "d-flex align-items-center", style: { gap: "8px" }, children: [_jsx("div", { style: {
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor: "#808080",
                                        } }), _jsx("span", { children: "Incoming Call:" })] }), _jsx("span", { style: { fontWeight: "600", color: "#333" }, children: formatSpeedToCareDuration(callDuration) })] }), _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsxs("div", { className: "d-flex align-items-center", style: { gap: "8px" }, children: [_jsx("div", { style: {
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor: "#00abc8",
                                        } }), _jsx("span", { children: "Time to Read:" })] }), _jsx("span", { style: { fontWeight: "600", color: "#333" }, children: formatSpeedToCareDuration(readTime) })] }), _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsxs("div", { className: "d-flex align-items-center", style: { gap: "8px" }, children: [_jsx("div", { style: {
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor: "#ff9e18",
                                        } }), _jsx("span", { children: "Time to Callback:" })] }), _jsx("span", { style: { fontWeight: "600", color: "#333" }, children: formatSpeedToCareDuration(callbackTime) })] }), _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsxs("div", { className: "d-flex align-items-center", style: { gap: "8px" }, children: [_jsx("div", { style: {
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor: "#3f2b56",
                                        } }), _jsx("span", { children: "Callback Duration:" })] }), _jsx("span", { style: { fontWeight: "600", color: "#333" }, children: formatSpeedToCareDuration(callbackDuration) })] }), _jsx("div", { style: {
                            borderTop: "1px solid #e0e0e0",
                            paddingTop: "8px",
                            marginTop: "4px",
                        }, children: _jsxs("div", { style: {
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }, children: [_jsxs("div", { className: "d-flex align-items-center", style: { gap: "8px" }, children: [_jsx("div", { style: {
                                                width: "8px",
                                                height: "8px",
                                                borderRadius: "50%",
                                                backgroundColor: "#374B94",
                                            } }), _jsx("span", { style: { fontWeight: "600" }, children: "Total Time:" })] }), _jsx("span", { style: {
                                        fontWeight: "700",
                                        color: "#374B94",
                                        fontSize: "14px",
                                    }, children: formatSpeedToCareDuration(totalTime) })] }) })] }) }));
    };
    const CustomTooltip = () => {
        if (!showTooltip || showTooltipAsInfo)
            return null;
        return (_jsx("div", { style: {
                position: "fixed",
                left: mousePosition.x + 10,
                top: mousePosition.y - 10,
                backgroundColor: "white",
                color: "#333",
                padding: "16px 20px",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: "500",
                zIndex: 1000,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                maxWidth: "300px",
                border: "1px solid #e0e0e0",
                pointerEvents: "none",
            }, children: _jsxs("div", { className: "d-flex flex-column", style: { gap: "6px" }, children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsxs("div", { className: "d-flex align-items-center", style: { gap: "8px" }, children: [_jsx("div", { style: {
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor: "#808080",
                                        } }), _jsx("span", { children: "Incoming Call:" })] }), _jsx("span", { style: { fontWeight: "600", color: "#333" }, children: formatSpeedToCareDuration(callDuration) })] }), _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsxs("div", { className: "d-flex align-items-center", style: { gap: "8px" }, children: [_jsx("div", { style: {
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor: "#00abc8",
                                        } }), _jsx("span", { children: "Time to Read:" })] }), _jsx("span", { style: { fontWeight: "600", color: "#333" }, children: formatSpeedToCareDuration(readTime) })] }), _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsxs("div", { className: "d-flex align-items-center", style: { gap: "8px" }, children: [_jsx("div", { style: {
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor: "#ff9e18",
                                        } }), _jsx("span", { children: "Time to Callback:" })] }), _jsx("span", { style: { fontWeight: "600", color: "#333" }, children: formatSpeedToCareDuration(callbackTime) })] }), _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsxs("div", { className: "d-flex align-items-center", style: { gap: "8px" }, children: [_jsx("div", { style: {
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor: "#3f2b56",
                                        } }), _jsx("span", { children: "Callback Duration:" })] }), _jsx("span", { style: { fontWeight: "600", color: "#333" }, children: formatSpeedToCareDuration(callbackDuration) })] }), _jsx("div", { style: {
                            borderTop: "1px solid #e0e0e0",
                            paddingTop: "8px",
                            marginTop: "4px",
                        }, children: _jsxs("div", { style: {
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }, children: [_jsxs("div", { className: "d-flex align-items-center", style: { gap: "8px" }, children: [_jsx("div", { style: {
                                                width: "8px",
                                                height: "8px",
                                                borderRadius: "50%",
                                                backgroundColor: "#374B94",
                                            } }), _jsx("span", { style: { fontWeight: "600" }, children: "Total Time:" })] }), _jsx("span", { style: {
                                        fontWeight: "700",
                                        color: "#374B94",
                                        fontSize: "14px",
                                    }, children: formatSpeedToCareDuration(totalTime) })] }) })] }) }));
    };
    if (!hasMinimumData) {
        return (_jsx("div", { className: "d-flex align-items-center justify-content-center", style: {
                width: "100%",
                height: compact ? "24px" : "32px",
                color: "#999",
                fontSize: compact ? "11px" : "12px",
                fontStyle: "italic",
            }, children: "No timeline data" }));
    }
    return (_jsxs("div", { style: { width: "100%", position: "relative" }, children: [_jsx(CustomTooltip, {}), _jsx(PersistentInfo, {}), showLegend && !showTooltipAsInfo && (_jsxs("div", { className: "d-flex flex-wrap justify-content-center", style: {
                    gap: "16px",
                    marginBottom: "8px",
                }, children: [_jsxs("div", { className: "d-flex align-items-center", style: { gap: "4px" }, children: [_jsx("div", { style: {
                                    width: "8px",
                                    height: "8px",
                                    borderRadius: "50%",
                                    backgroundColor: "#808080",
                                } }), _jsx("span", { style: { fontSize: "12px", color: "#666" }, children: "Call Duration" })] }), _jsxs("div", { className: "d-flex align-items-center", style: { gap: "4px" }, children: [_jsx("div", { style: {
                                    width: "8px",
                                    height: "8px",
                                    borderRadius: "50%",
                                    backgroundColor: "#00abc8",
                                } }), _jsx("span", { style: { fontSize: "12px", color: "#666" }, children: "Time to Read" })] }), _jsxs("div", { className: "d-flex align-items-center", style: { gap: "4px" }, children: [_jsx("div", { style: {
                                    width: "8px",
                                    height: "8px",
                                    borderRadius: "50%",
                                    backgroundColor: "#ff9e18",
                                } }), _jsx("span", { style: { fontSize: "12px", color: "#666" }, children: "Time to Callback" })] }), _jsxs("div", { className: "d-flex align-items-center", style: { gap: "4px" }, children: [_jsx("div", { style: {
                                    width: "8px",
                                    height: "8px",
                                    borderRadius: "50%",
                                    backgroundColor: "#3f2b56",
                                } }), _jsx("span", { style: { fontSize: "12px", color: "#666" }, children: "Callback Duration" })] })] })), !hideGraph && (_jsxs("div", { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, onMouseMove: handleMouseMove, className: "d-flex", style: {
                    width: "100%",
                    height: "24px",
                    border: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    cursor: "pointer",
                    position: "relative",
                    backgroundColor: "#f8f9fa",
                }, children: [_jsx("div", { className: "d-flex justify-content-end align-items-center", style: {
                            width: "30%",
                            height: "100%",
                            position: "relative",
                        }, children: callDuration > 0 && (_jsx("div", { className: "d-flex align-items-center justify-content-center", style: {
                                width: `${getCallDurationWidth()}%`,
                                backgroundColor: "#808080",
                                height: "100%",
                                marginLeft: "auto",
                            }, children: getCallDurationWidth() > (compact ? 15 : 8) && (_jsx("span", { style: {
                                    color: "white",
                                    fontSize: compact ? "8px" : "10px",
                                    fontWeight: "bold",
                                    whiteSpace: "nowrap",
                                }, children: formatSpeedToCareDuration(callDuration) })) })) }), _jsx("div", { style: {
                            width: "2px",
                            height: "100%",
                            backgroundColor: "#374B94",
                            zIndex: 10,
                        } }), _jsxs("div", { className: "d-flex align-items-center", style: {
                            width: "68%",
                            height: "100%",
                            position: "relative",
                        }, children: [readTime > 0 && (_jsx("div", { className: "d-flex align-items-center justify-content-center", style: {
                                    width: `${getReadTimeWidth()}%`,
                                    backgroundColor: "#00abc8",
                                    height: "100%",
                                }, children: getReadTimeWidth() > (compact ? 15 : 8) && (_jsx("span", { style: {
                                        color: "white",
                                        fontSize: compact ? "8px" : "10px",
                                        fontWeight: "bold",
                                        whiteSpace: "nowrap",
                                    }, children: formatSpeedToCareDuration(readTime) })) })), adjustedCallbackTime > 0 && (_jsxs("div", { className: "d-flex align-items-center justify-content-center", style: {
                                    width: `${getCallbackTimeWidth()}%`,
                                    backgroundColor: "#ff9e18",
                                    height: "100%",
                                    position: "relative",
                                }, children: [_jsx("div", { style: {
                                            position: "absolute",
                                            top: "-2px",
                                            right: "-1px",
                                            width: "4px",
                                            height: "4px",
                                            backgroundColor: "#ffcd00",
                                            borderRadius: "50%",
                                            opacity: 0.8,
                                        } }), getCallbackTimeWidth() > (compact ? 15 : 8) && (_jsx("span", { style: {
                                            color: "white",
                                            fontSize: compact ? "8px" : "10px",
                                            fontWeight: "bold",
                                            whiteSpace: "nowrap",
                                        }, children: formatSpeedToCareDuration(adjustedCallbackTime) }))] })), callbackDuration > 0 && (_jsx("div", { className: "d-flex align-items-center justify-content-center", style: {
                                    width: `${getCallbackDurationWidth()}%`,
                                    backgroundColor: "#3f2b56",
                                    height: "100%",
                                }, children: getCallbackDurationWidth() > (compact ? 15 : 8) && (_jsx("span", { style: {
                                        color: "white",
                                        fontSize: compact ? "8px" : "10px",
                                        fontWeight: "bold",
                                        whiteSpace: "nowrap",
                                    }, children: formatSpeedToCareDuration(callbackDuration) })) }))] })] }))] }));
};
export default SpeedToCare;
//# sourceMappingURL=SpeedToCare.js.map